// The console's chart set (ADR 0002: recharts 3 lives only in the kit).
// Every chart is a <figure> with an accessible name and a visually hidden
// data table, draws inside a plot that grows to fill its card, prints names
// on or beside its marks (never a numbered key), and shares one tooltip,
// one legend, and one hover focus that dims the rest.
//
//   TimeChart       time: one series as an area from zero, two or more as lines; an optional target line
//   BarList         a ranking or a comparison by category: horizontal bars, names above, values printed;
//                   diverging from zero (or from a reference) when values cross it
//   ColumnChart2    ordered bins (a histogram, an age band): columns with the value on top
//   DonutChart      part to whole of two to six parts (the tail is summed as "Lainnya")
//   FunnelChart2    a process in steps: one continuous funnel shape, the biggest drop named
//   HeatTiles       a ranking as tiles, name and value printed, tone stepped by rank
//   StackedColumns  counts by category and status (a few series), stacked, legend toggles
//   PairedBars2     own against competitor per group, two bars per row, legend toggles
//   DeviationList   averages, rates, durations per category: bars out from their average, the difference printed
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SrTable, type SrTableProps } from "./frame";
import { textWidthBound, tickAxisWidth } from "./Cartesian";
import { RechartsTip, VizLegend, VizTip, useHoverTip, useSeriesState, vizColor, type LegendItem } from "../viz/Interact";

const FILL = { width: "100%", height: "100%" } as const;

export type PlotSize = "sm" | "md" | "lg";
export type VizFormat = (n: number) => string;

export interface VizDatum {
  label: string;
  value: number;
}

interface ShellProps {
  label: string;
  kind: string;
  table: SrTableProps;
  legend?: ReactNode;
  /** Text under the plot, a short key. */
  note?: ReactNode;
  children: ReactNode;
}

/** The figure every chart draws in: legend, the plot (which grows), an optional note, the hidden table. */
function Shell({ label, kind, table, legend, note, children }: ShellProps) {
  return (
    <figure className="viz-chart" data-chart={kind} aria-label={label}>
      {legend}
      {children}
      {note ? <p className="viz-chart-note">{note}</p> : null}
      <figcaption className="viz-sr">
        <SrTable {...table} />
      </figcaption>
    </figure>
  );
}

/** An SVG plot: a box that grows with its card and holds the chart as an out-of-flow layer. */
function SvgPlot({ size = "md", children }: { size?: PlotSize; children: ReactNode }) {
  return (
    <div className="viz-plot" data-size={size}>
      <div className="viz-plot-canvas" aria-hidden="true">
        {children}
      </div>
    </div>
  );
}

interface EdgeTickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value?: unknown };
  first?: string;
  last?: string;
}

/** An x tick that anchors the first label at its start and the last at its end, so neither leaves the plot. */
function EdgeTick({ x = 0, y = 0, payload, first, last }: EdgeTickProps) {
  const v = String(payload?.value ?? "");
  const anchor = v === first ? "start" : v === last ? "end" : "middle";
  return (
    <text x={Number(x)} y={Number(y)} dy={16} textAnchor={anchor} className="viz-tick">
      {v}
    </text>
  );
}

/** Evenly spaced ticks from zero for counts: integer steps, the last at or above the largest value. */
export function countTicks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const step = Math.max(1, Math.ceil(max / 4));
  const out: number[] = [];
  for (let t = 0; t < max + step; t += step) out.push(t);
  return out;
}

/**
 * A category tick: centred under its band and wrapped at words onto at most
 * two lines; where a band is narrower than its longest word, every label
 * turns to a slant so no two names touch.
 */
function CategoryTick({ x = 0, y = 0, payload, width, visibleTicksCount }: { x?: number | string; y?: number | string; payload?: { value?: unknown }; width?: number; visibleTicksCount?: number }) {
  const text = String(payload?.value ?? "");
  const words = text.split(/\s+/);
  const band = width && visibleTicksCount ? width / visibleTicksCount : Infinity;
  const widest = Math.max(...words.map((w) => textWidthBound(w)));
  if (widest > band - 4) {
    return (
      <text x={Number(x)} y={Number(y)} dy={12} textAnchor="end" transform={`rotate(-40 ${Number(x)} ${Number(y) + 12})`} className="viz-tick">
        {text}
      </text>
    );
  }
  const lines = words.length <= 1 ? words : [words.slice(0, Math.ceil(words.length / 2)).join(" "), words.slice(Math.ceil(words.length / 2)).join(" ")];
  return (
    <text x={Number(x)} y={Number(y)} textAnchor="middle" className="viz-tick">
      {lines.map((line, i) => (
        <tspan key={i} x={Number(x)} dy={i === 0 ? 14 : 16}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

/** A stage name left of a vertical plot, right-aligned, wrapped at words onto at most two lines inside its lane. */
function NameTick({ x = 0, y = 0, payload, width = 120 }: { x?: number | string; y?: number | string; payload?: { value?: unknown }; width?: number }) {
  const text = String(payload?.value ?? "");
  const lines = textWidthBound(text) <= width - 8 ? [text] : (() => {
    const words = text.split(/\s+/);
    const half = Math.ceil(words.length / 2);
    return [words.slice(0, half).join(" "), words.slice(half).join(" ")];
  })();
  return (
    <text x={Number(x)} y={Number(y)} textAnchor="end" className="viz-tick">
      {lines.map((line, i) => (
        <tspan key={i} x={Number(x)} dy={i === 0 ? (lines.length === 1 ? 4 : -4) : 16}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

/**
 * Evenly spaced round ticks over lo..hi whose printed labels are all
 * different: fewer, wider steps are taken until no two ticks print alike.
 */
export function valueTicks(lo: number, hi: number, format: VizFormat): number[] {
  if (hi <= lo) hi = lo + (Math.abs(lo) || 1);
  for (const count of [4, 3, 2]) {
    const raw = (hi - lo) / count;
    const pow = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((st) => st >= raw) ?? 10 * pow;
    const start = Math.floor(lo / step) * step;
    const ticks: number[] = [];
    for (let t = start; t < hi + step * 0.999; t += step) ticks.push(Number(t.toPrecision(12)));
    if (new Set(ticks.map(format)).size === ticks.length) return ticks;
  }
  return [lo, hi];
}

/** Three round ticks spanning lo..hi (the ends and the middle), so a narrow axis never crowds. */
export function threeTicks(lo: number, hi: number): number[] {
  const range = hi - lo || Math.abs(hi) || 1;
  const pow = 10 ** Math.floor(Math.log10(range / 2));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((st) => st * 2 >= range) ?? 10 * pow;
  const start = Math.floor(lo / step) * step;
  return [start, start + step, start + 2 * step];
}

/** A short form of a formatter for axis ticks: 1.250 reads 1,3 rb; values with a unit word stay as they are. */
export function compactFormat(format: VizFormat): VizFormat {
  const c = new Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 });
  return (n: number) => {
    const full = format(n);
    if (full.length <= 6) return full;
    if (full.includes("Rp")) return `Rp ${c.format(n)}`;
    if (/[A-Za-z%]/.test(full)) return full;
    return c.format(n);
  };
}

/** A point's name beside it, on the side the plan gave it. */
function PointLabel({ x = 0, y = 0, index = 0, sides, names }: { x?: number | string; y?: number | string; index?: number; sides: ("right" | "left" | "none")[]; names: string[] }) {
  const side = sides[index] ?? "right";
  if (side === "none") return null;
  return (
    <text x={Number(x) + (side === "left" ? -2 : 14)} y={Number(y) + 6} dy={4} textAnchor={side === "left" ? "end" : "start"} className="viz-tick">
      {names[index]}
    </text>
  );
}

/** A value label beside a stair point, on the side the connector does not arrive from. */
function StepLabel({ x = 0, y = 0, index = 0, values, format }: { x?: number | string; y?: number | string; index?: number; values: number[]; format: VizFormat; value?: unknown }) {
  const prev = values[index - 1];
  const left = prev !== undefined && prev > values[index]!;
  return (
    <text x={Number(x) + (left ? -10 : 10)} y={Number(y)} dy={4} textAnchor={left ? "end" : "start"} className="viz-tick">
      {format(values[index]!)}
    </text>
  );
}

/** A value tick under a horizontal value axis; the ends anchor inward so they stay inside the plot. */
function EdgeValueTick({ x = 0, y = 0, payload, format, last, first = 0 }: { x?: number | string; y?: number | string; payload?: { value?: unknown }; format: VizFormat; last: number; first?: number }) {
  const v = Number(payload?.value ?? 0);
  return (
    <text x={Number(x)} y={Number(y)} dy={16} textAnchor={v === last ? "end" : v === first ? "start" : "middle"} className="viz-tick">
      {format(v)}
    </text>
  );
}

/** A value tick on one line, right-aligned against the plot: never wrapped at a space. */
function ValueTick({ x = 0, y = 0, payload, format }: { x?: number | string; y?: number | string; payload?: { value?: unknown }; format: VizFormat }) {
  return (
    <text x={Number(x)} y={Number(y)} dy={4} textAnchor="end" className="viz-tick">
      {format(Number(payload?.value ?? 0))}
    </text>
  );
}

/* ---------------------------------------------------------------------
 * TimeChart
 * ------------------------------------------------------------------- */

export interface TimeSeries {
  key: string;
  label: string;
}

export interface TimeChartProps {
  label: string;
  /** One row per period, in time order. */
  data: ({ label: string } & Record<string, number | string>)[];
  series: TimeSeries[];
  format: VizFormat;
  /** A level target, drawn as a dashed line and named in the legend. */
  target?: { value: number; label: string };
  size?: PlotSize;
  categoryLabel?: string;
}

/** One series reads as an area from zero; two or more as lines with a toggling legend. */
export function TimeChart({ label, data, series, format, target, size = "md", categoryLabel = "Periode" }: TimeChartProps) {
  const { hidden, focus, toggle, setFocus } = useSeriesState();
  const tight = (() => {
    const v = data.flatMap((row) => series.map((s) => Number(row[s.key]))).filter(Number.isFinite);
    if (v.length === 0) return false;
    const lo = Math.min(...v);
    const hi = Math.max(...v);
    return lo > 0 && hi > 0 && lo / hi > 0.8;
  })();
  // A series that moves within a narrow band reads as a line on a zoomed axis; an area must start at zero.
  const single = series.length === 1 && !tight;
  const colors = Object.fromEntries(series.map((s, i) => [s.key, vizColor(i)]));
  const names = Object.fromEntries(series.map((s) => [s.key, s.label]));
  const shown = series.filter((s) => !hidden.has(s.key));
  const values = data.flatMap((row) => shown.map((s) => Number(row[s.key])).filter(Number.isFinite));
  if (target) values.push(target.value);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const yTicks = valueTicks(single && min >= 0 ? 0 : min, max, format);
  const width = tickAxisWidth(yTicks, format);
  // Evenly spaced months: every label when they fit, else every second or third, never a skipped gap.
  const interval = data.length <= 6 ? 0 : Math.ceil(data.length / 4) - 1;
  const years = new Set(data.map((d) => String(d.label).match(/\b(\d{4})$/)?.[1] ?? "x"));
  const shortLabel = years.size === 1 && !years.has("x") ? (l: string) => l.replace(/\s+\d{4}$/, "") : (l: string) => l;
  const tdata = data.map((row) => ({ ...row, label: shortLabel(String(row.label)) }));
  const first = tdata[0]?.label;
  const last = tdata[tdata.length - 1]?.label;
  const legendItems: LegendItem[] = [
    ...series.map((s) => ({ key: s.key, label: s.label, color: colors[s.key]! })),
    ...(target ? [{ key: "__target", label: target.label, color: "var(--color-ink-muted)", fixed: true }] : []),
  ];
  const legend = series.length > 1 || (target && single) || (target && tight) ? <VizLegend label={`Seri ${label}`} items={legendItems} hidden={hidden} onToggle={toggle} onFocus={setFocus} /> : null;
  const dim = (key: string) => (focus !== null && focus !== key ? 0.24 : 1);
  return (
    <Shell
      label={label}
      kind={single ? "area" : "line"}
      note={tight && series.length === 1 ? "Sumbu tidak dimulai dari nol: nilainya bergerak dalam rentang sempit." : undefined}
      legend={legend}
      table={{
        caption: label,
        head: [categoryLabel, ...series.map((s) => s.label), ...(target ? [target.label] : [])],
        rows: data.map((row) => [row.label, ...series.map((s) => format(Number(row[s.key]) || 0)), ...(target ? [format(target.value)] : [])]),
      }}
    >
      <SvgPlot size={size}>
        <ComposedChart responsive style={FILL} data={tdata} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} accessibilityLayer={false}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} interval={interval} height={28} tick={<EdgeTick first={first} last={last} />} />
          <YAxis width={width} tickLine={false} axisLine={false} ticks={yTicks} domain={[yTicks[0]!, yTicks[yTicks.length - 1]!]} tick={<ValueTick format={format} />} />
          <Tooltip cursor={{ stroke: "var(--color-border-strong)" }} content={<RechartsTip format={format} names={names} colors={colors} />} isAnimationActive={false} />
          {target ? <ReferenceLine y={target.value} stroke="var(--color-ink-muted)" strokeDasharray="4 4" ifOverflow="extendDomain" /> : null}
          {series.map((s) =>
            hidden.has(s.key) ? null : single ? (
              <Area
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.label}
                stroke={colors[s.key]}
                strokeWidth={2}
                fill="var(--viz-area)"
                fillOpacity={1}
                activeDot={{ r: 4, strokeWidth: 0, fill: colors[s.key] }}
                dot={false}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.label}
                stroke={colors[s.key]}
                strokeWidth={2}
                strokeOpacity={dim(s.key)}
                activeDot={{ r: 4, strokeWidth: 0, fill: colors[s.key] }}
                dot={false}
                isAnimationActive={false}
              />
            ),
          )}
        </ComposedChart>
      </SvgPlot>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * BarList
 * ------------------------------------------------------------------- */

export interface BarListProps {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  categoryLabel: string;
  valueLabel: string;
  /** Sort largest first (a ranking). Off keeps the given order (a sequence). */
  sort?: boolean;
  /** End of the scale when it is fixed (100 for a percent of a whole). */
  max?: number;
  /** Draw each bar from this value instead of zero (an average), naming it in the note. */
  reference?: { value: number; label: string };
  /** Rows drawn at most; the rest are named in the note. */
  limit?: number;
  /** The rows are the steps of a process: the tooltip adds each step against the one before. */
  steps?: boolean;
}

function niceEnd(n: number): number {
  if (n <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(n));
  const steps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  return (steps.find((s) => s * pow >= n) ?? 10) * pow;
}

/**
 * Horizontal bars. The name sits on its own line above the bar, so a long
 * name is never cut or numbered; the value prints at the line's end. Values
 * that cross zero (or the reference) draw out from a centre line, the side
 * below in the second hue.
 */
export function BarList({ label, data, format, categoryLabel, valueLabel, sort = true, max, reference, limit = 10, steps = false }: BarListProps) {
  const tip = useHoverTip();
  const ordered = sort ? [...data].sort((a, b) => b.value - a.value) : data;
  const rows = ordered.slice(0, limit);
  const base = reference?.value ?? 0;
  const offsets = rows.map((d) => d.value - base);
  const crosses = offsets.some((o) => o < 0);
  const hi = Math.max(0, ...offsets);
  const lo = Math.min(0, ...offsets);
  const end = max !== undefined && !crosses && base === 0 ? max : crosses ? Math.max(hi, -lo) : niceEnd(hi);
  const total = data.reduce((s, d) => s + d.value, 0);
  const rank = (i: number) => `Peringkat ${i + 1} dari ${ordered.length}`;
  const note =
    ordered.length > rows.length
      ? `${rows.length} ${sort ? "teratas" : "pertama"} dari ${ordered.length}. Daftar lengkap di Rincian.`
      : reference
        ? `Batang dihitung dari ${reference.label}, ${format(reference.value)}.`
        : undefined;
  return (
    <Shell
      label={label}
      kind="bars"
      note={note}
      table={{ caption: label, head: [categoryLabel, valueLabel], rows: ordered.map((d) => [d.label, format(d.value)]) }}
    >
      <div className="viz-plot viz-bars" data-crosses={crosses ? "" : undefined} ref={tip.plot}>
        {rows.map((d, i) => {
          const off = offsets[i]!;
          const share = end > 0 ? Math.min(1, Math.abs(off) / end) : 0;
          const style = { "--share": share } as CSSProperties;
          return (
            <div className="viz-bar" key={`${d.label}-${i}`} data-below={off < 0 ? "" : undefined} {...tip.bind(i)}>
              <span className="viz-bar-name">{d.label}</span>
              <span className="viz-bar-value">{format(d.value)}</span>
              <span className="viz-bar-track" aria-hidden="true">
                <span className="viz-bar-fill" style={style} />
              </span>
              {steps && i > 0 && rows[i - 1]!.value > 0 ? (
                <span className="viz-bar-step">{`${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 }).format((d.value / rows[i - 1]!.value) * 100)}% dari ${rows[i - 1]!.label}`}</span>
              ) : null}
            </div>
          );
        })}
        {tip.layer((i) => {
          const d = rows[i]!;
          const extra =
            reference !== undefined
              ? [{ label: `Selisih dari ${reference.label}`, value: `${d.value - base >= 0 ? "+" : ""}${format(d.value - base)}` }]
              : total > 0 && !crosses && max === undefined
                ? [{ label: "Porsi dari jumlah", value: `${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 }).format((d.value / total) * 100)}%` }]
                : [];
          const prev = rows[i - 1];
          const step =
            steps && prev && prev.value > 0
              ? [{ label: `Dari ${prev.label}`, value: `${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 }).format((d.value / prev.value) * 100)}%` }]
              : [];
          return (
            <VizTip
              title={d.label}
              rows={[{ label: valueLabel, value: format(d.value), color: d.value - base < 0 ? "var(--viz-3)" : "var(--viz-1)" }, ...(steps ? step : extra), ...(steps ? [] : [{ label: rank(i), value: "" }])].filter((r) => r.label)}
            />
          );
        })}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * ColumnChart2 (HTML columns for ordered bins)
 * ------------------------------------------------------------------- */

export interface ColumnChart2Props {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  categoryLabel: string;
  valueLabel: string;
}

/** Ordered bins as columns, each value printed on top and each name under it, wrapped at a word. */
export function ColumnChart2({ label, data, format, categoryLabel, valueLabel }: ColumnChart2Props) {
  const tip = useHoverTip();
  const end = niceEnd(Math.max(0, ...data.map((d) => d.value)));
  const total = data.reduce((s, d) => s + d.value, 0);
  const pct = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });
  return (
    <Shell label={label} kind="columns" table={{ caption: label, head: [categoryLabel, valueLabel], rows: data.map((d) => [d.label, format(d.value)]) }}>
      <div className="viz-plot viz-cols" ref={tip.plot} style={{ "--cols": data.length } as CSSProperties}>
        {data.map((d, i) => (
          <div className="viz-col" key={`${d.label}-${i}`} {...tip.bind(i)}>
            <div className="viz-col-well" style={{ "--share": end > 0 ? Math.max(0, d.value) / end : 0 } as CSSProperties}>
              <span className="viz-col-bar" />
              <span className="viz-col-value">{format(d.value)}</span>
            </div>
            <span className="viz-col-name">{d.label}</span>
          </div>
        ))}
        {tip.layer((i) => {
          const d = data[i]!;
          return (
            <VizTip
              title={d.label}
              rows={[
                { label: valueLabel, value: format(d.value), color: "var(--viz-1)" },
                ...(total > 0 ? [{ label: "Porsi dari jumlah", value: `${pct.format((d.value / total) * 100)}%` }] : []),
              ]}
            />
          );
        })}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * DonutChart
 * ------------------------------------------------------------------- */

/** Parts beyond this many are summed into one "Lainnya" part. */
export const DONUT_PARTS_MAX = 6;

/** The parts a donut draws: largest first, the tail past `max` summed as "Lainnya". */
export function groupParts(data: VizDatum[], max = DONUT_PARTS_MAX): VizDatum[] {
  const sorted = [...data].filter((d) => d.value > 0).sort((a, b) => b.value - a.value);
  if (sorted.length <= max) return sorted;
  const head = sorted.slice(0, max - 1);
  const rest = sorted.slice(max - 1).reduce((s, d) => s + d.value, 0);
  return [...head, { label: "Lainnya", value: rest }];
}

/** The side of the largest square a box holds, measured; the ring's drawing box takes it. */
function useSquare() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [side, setSide] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const read = () => setSide(Math.max(0, Math.floor(Math.min(el.clientWidth, el.clientHeight))));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const style: CSSProperties | undefined = side ? { inlineSize: side, blockSize: side } : undefined;
  return { ref, style };
}

const PART_COLORS = [
  "var(--viz-1)",
  "var(--viz-2)",
  "var(--viz-3)",
  "var(--viz-6)",
  "color-mix(in oklab, var(--viz-1) 50%, var(--color-surface))",
  "color-mix(in oklab, var(--viz-2) 50%, var(--color-surface))",
] as const;

export interface DonutChartProps {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  categoryLabel: string;
  valueLabel: string;
}

/** A ring of parts with the total in its centre; hover a part (or its legend row) and the centre reads it. */
export function DonutChart({ label, data, format, categoryLabel, valueLabel }: DonutChartProps) {
  const parts = groupParts(data);
  const [active, setActive] = useState<number | null>(null);
  const total = parts.reduce((s, d) => s + d.value, 0);
  const pct = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });
  const share = (v: number) => (total > 0 ? `${pct.format((v / total) * 100)}%` : "0%");
  const focus = active === null ? null : parts[active]!;
  const square = useSquare();
  return (
    <Shell
      label={label}
      kind="donut"
      table={{ caption: label, head: [categoryLabel, valueLabel, "Porsi"], rows: data.map((d) => [d.label, format(d.value), share(d.value)]) }}
    >
      <div className="viz-donut">
        <div className="viz-plot viz-donut-plot" data-size="sm" ref={square.ref}>
          <div className="viz-plot-canvas" aria-hidden="true" style={square.style}>
            <PieChart responsive style={FILL} accessibilityLayer={false}>
              <Pie
                data={parts}
                dataKey="value"
                nameKey="label"
                innerRadius="64%"
                outerRadius="96%"
                paddingAngle={parts.length > 1 ? 1 : 0}
                stroke="var(--color-surface)"
                strokeWidth={2}
                startAngle={90}
                endAngle={-270}
                isAnimationActive={false}
                onMouseLeave={() => setActive(null)}
              >
                {parts.map((d, i) => (
                  <Cell key={d.label} fill={PART_COLORS[i % PART_COLORS.length]} fillOpacity={active === null || active === i ? 1 : 0.28} onMouseEnter={() => setActive(i)} />
                ))}
              </Pie>
            </PieChart>
          </div>
          <div className="viz-donut-center">
            <span className="viz-donut-value" data-long={compactFormat(format)(focus ? focus.value : total).length > 6 ? "" : undefined}>
              {compactFormat(format)(focus ? focus.value : total)}
            </span>
            <span className="viz-donut-label">{focus ? share(focus.value) : "Jumlah"}</span>
          </div>
        </div>
        <ul className="viz-donut-legend">
          {parts.map((d, i) => (
            <li key={d.label}>
              <button
                type="button"
                className="viz-donut-key"
                data-active={active === i ? "" : undefined}
                aria-pressed={active === i}
                onPointerEnter={() => setActive(i)}
                onPointerLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                onClick={() => setActive(active === i ? null : i)}
              >
                <span className="viz-swatch" style={{ background: PART_COLORS[i % PART_COLORS.length] }} aria-hidden="true" />
                <span className="viz-donut-name">{d.label}</span>
                <span className="viz-donut-num">{format(d.value)}</span>
                <span className="viz-donut-share">{share(d.value)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * FunnelChart2
 * ------------------------------------------------------------------- */

export interface FunnelChart2Props {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  valueLabel: string;
}

/**
 * The steps of a process as one continuous funnel: each band's top is its
 * own value and its foot narrows to the next step's, so the shape itself
 * shows where people leave. Name on the left, value and share of the first
 * step on the right; the step with the biggest drop is named once.
 */
export function FunnelChart2({ label, data, format, valueLabel }: FunnelChart2Props) {
  const tip = useHoverTip();
  const top = Math.max(1, ...data.map((d) => d.value));
  const first = data[0]?.value ?? 0;
  const pct = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });
  const ofFirst = (v: number) => (first > 0 ? `${pct.format((v / first) * 100)}%` : "0%");
  const conversion = data.map((d, i) => (i === 0 ? null : data[i - 1]!.value > 0 ? d.value / data[i - 1]!.value : null));
  let drop = -1;
  let worst = 1;
  conversion.forEach((c, i) => {
    if (c !== null && c < worst) {
      worst = c;
      drop = i;
    }
  });
  return (
    <Shell
      label={label}
      kind="funnel"
      table={{
        caption: label,
        head: ["Tahap", valueLabel, "Dari tahap pertama", "Dari tahap sebelumnya"],
        rows: data.map((d, i) => [d.label, format(d.value), ofFirst(d.value), conversion[i] === null ? "" : `${pct.format(conversion[i]! * 100)}%`]),
      }}
    >
      <div className="viz-plot viz-funnel" ref={tip.plot}>
        {data.map((d, i) => {
          const w = (d.value / top) * 100;
          const next = data[i + 1];
          const w2 = next ? (next.value / top) * 100 : w;
          const shape = `polygon(${50 - w / 2}% 0, ${50 + w / 2}% 0, ${50 + w2 / 2}% 100%, ${50 - w2 / 2}% 100%)`;
          return (
            <div className="viz-step" key={`${d.label}-${i}`} {...tip.bind(i)}>
              <span className="viz-step-name">
                {d.label}
                {i === drop ? <span className="viz-step-drop">Turun terbesar dari tahap sebelumnya</span> : null}
              </span>
              <span className="viz-step-shape" aria-hidden="true">
                <span style={{ clipPath: shape }} />
              </span>
              <span className="viz-step-num">
                <span className="viz-step-value">{format(d.value)}</span>
                <span className="viz-step-share">{ofFirst(d.value)}</span>
              </span>
            </div>
          );
        })}
        {tip.layer((i) => {
          const d = data[i]!;
          const c = conversion[i];
          return (
            <VizTip
              title={d.label}
              rows={[
                { label: valueLabel, value: format(d.value), color: "var(--viz-1)" },
                { label: "Dari tahap pertama", value: ofFirst(d.value) },
                ...(c !== null && c !== undefined ? [{ label: "Dari tahap sebelumnya", value: `${pct.format(c * 100)}%` }] : []),
              ]}
            />
          );
        })}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * HeatTiles
 * ------------------------------------------------------------------- */

export interface HeatTilesProps {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  categoryLabel: string;
  valueLabel: string;
  /** Rows drawn at most, largest first. */
  limit?: number;
}

/** Columns for a tile grid of n equal tiles: as wide as possible with the fewest empty cells. */
export function heatCols(n: number): number {
  if (n <= 4) return Math.max(1, n);
  let best = 4;
  let waste = Infinity;
  for (const c of [4, 3, 2]) {
    const w = Math.ceil(n / c) * c - n;
    if (w < waste) {
      waste = w;
      best = c;
    }
  }
  return best;
}

/** The five tone steps, from the lowest value to the highest. */
export function heatSteps(values: number[]): number[] {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return values.map((v) => (hi === lo ? 3 : 1 + Math.round(((v - lo) / (hi - lo)) * 4)));
}

/** A ranking as tiles: each carries its name and value, the tone deepens with the value. */
export function HeatTiles({ label, data, format, categoryLabel, valueLabel, limit = 12 }: HeatTilesProps) {
  const tip = useHoverTip();
  const ordered = [...data].sort((a, b) => b.value - a.value);
  const rows = ordered.slice(0, limit);
  const steps = heatSteps(rows.map((d) => d.value));
  const mean = data.length ? data.reduce((s, d) => s + d.value, 0) / data.length : 0;
  return (
    <Shell
      label={label}
      kind="heat"
      note={ordered.length > rows.length ? `${rows.length} teratas dari ${ordered.length}. Warna lebih pekat, nilai lebih tinggi.` : "Warna lebih pekat, nilai lebih tinggi."}
      table={{ caption: label, head: [categoryLabel, valueLabel], rows: ordered.map((d) => [d.label, format(d.value)]) }}
    >
      <div className="viz-plot viz-heat" ref={tip.plot} style={{ "--cols": heatCols(rows.length) } as CSSProperties}>
        {rows.map((d, i) => (
          <div className="viz-heat-tile" key={`${d.label}-${i}`} data-step={steps[i]} {...tip.bind(i)}>
            <span className="viz-heat-name">{d.label}</span>
            <span className="viz-heat-value">{format(d.value)}</span>
          </div>
        ))}
        {tip.layer((i) => {
          const d = rows[i]!;
          const diff = d.value - mean;
          return (
            <VizTip
              title={d.label}
              rows={[
                { label: valueLabel, value: format(d.value), color: "var(--viz-1)" },
                { label: "Selisih dari rata-rata", value: `${diff >= 0 ? "+" : ""}${format(diff)}` },
                { label: `Peringkat ${i + 1} dari ${ordered.length}`, value: "" },
              ]}
            />
          );
        })}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * StackedColumns
 * ------------------------------------------------------------------- */

export interface StackedColumnsProps {
  label: string;
  data: ({ label: string } & Record<string, number | string>)[];
  series: TimeSeries[];
  format: VizFormat;
  categoryLabel: string;
  size?: PlotSize;
}

/** Counts by category, stacked by series (a status), with a toggling legend and the shared tooltip. */
export function StackedColumns({ label, data, series, format, categoryLabel, size = "sm" }: StackedColumnsProps) {
  const { hidden, focus, toggle, setFocus } = useSeriesState();
  const colors = Object.fromEntries(series.map((s, i) => [s.key, vizColor(i)]));
  const names = Object.fromEntries(series.map((s) => [s.key, s.label]));
  const totals = data.map((row) => series.filter((s) => !hidden.has(s.key)).reduce((sum, s) => sum + (Number(row[s.key]) || 0), 0));
  const width = tickAxisWidth([...totals, 0], format);
  const used = series.filter((s) => data.some((row) => (Number(row[s.key]) || 0) > 0));
  const legend =
    used.length > 1 ? (
      <VizLegend label={`Seri ${label}`} items={used.map((s) => ({ key: s.key, label: s.label, color: colors[s.key]! }))} hidden={hidden} onToggle={toggle} onFocus={setFocus} />
    ) : null;
  const ticks = countTicks(Math.max(0, ...totals));
  const visible = series.filter((s) => !hidden.has(s.key));
  return (
    <Shell
      label={label}
      kind="stacked"
      legend={legend}
      table={{ caption: label, head: [categoryLabel, ...series.map((s) => s.label)], rows: data.map((row) => [row.label, ...series.map((s) => format(Number(row[s.key]) || 0))]) }}
    >
      <SvgPlot size={size}>
        <BarChart responsive style={FILL} data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} accessibilityLayer={false}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} interval={0} height={64} tick={<CategoryTick />} />
          <YAxis width={width} tickLine={false} axisLine={false} allowDecimals={false} ticks={ticks} domain={[0, ticks[ticks.length - 1]!]} tickFormatter={format} tick={<ValueTick format={format} />} />
          <Tooltip cursor={{ fill: "var(--color-layer-1)" }} content={<RechartsTip format={format} names={names} colors={colors} />} isAnimationActive={false} />
          {visible.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="all"
              fill={colors[s.key]}
              fillOpacity={focus !== null && focus !== s.key ? 0.24 : 1}
              maxBarSize={56}
              radius={i === visible.length - 1 ? [4, 4, 0, 0] : 0}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </SvgPlot>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * PairedBars2
 * ------------------------------------------------------------------- */

export interface PairedDatum {
  label: string;
  a: number;
  b: number;
}

export interface PairedBars2Props {
  label: string;
  data: PairedDatum[];
  format: VizFormat;
  categoryLabel: string;
  aLabel: string;
  bLabel: string;
  limit?: number;
}

/** Two values per group (own against competitor), each on its own bar from zero, legend toggles either side. */
export function PairedBars2({ label, data, format, categoryLabel, aLabel, bLabel, limit = 8 }: PairedBars2Props) {
  const { hidden, focus, toggle, setFocus } = useSeriesState();
  const tip = useHoverTip();
  const rows = data.slice(0, limit);
  const end = niceEnd(Math.max(0, ...rows.flatMap((d) => [hidden.has("a") ? 0 : d.a, hidden.has("b") ? 0 : d.b])));
  const items: LegendItem[] = [
    { key: "a", label: aLabel, color: "var(--viz-1)" },
    { key: "b", label: bLabel, color: "var(--viz-2)" },
  ];
  return (
    <Shell
      label={label}
      kind="paired"
      legend={<VizLegend label={`Seri ${label}`} items={items} hidden={hidden} onToggle={toggle} onFocus={setFocus} />}
      note={data.length > rows.length ? `${rows.length} dari ${data.length}, selisih terbesar lebih dulu. Daftar lengkap di Rincian.` : undefined}
      table={{ caption: label, head: [categoryLabel, aLabel, bLabel], rows: data.map((d) => [d.label, format(d.a), format(d.b)]) }}
    >
      <div className="viz-plot viz-pairs" ref={tip.plot} data-focus={focus ?? undefined}>
        {rows.map((d, i) => (
          <div className="viz-pair" key={`${d.label}-${i}`} {...tip.bind(i)}>
            <span className="viz-bar-name">{d.label}</span>
            {(["a", "b"] as const).map((key) =>
              hidden.has(key) ? null : (
                <span className="viz-pair-line" data-series={key} key={key}>
                  <span className="viz-bar-track" aria-hidden="true">
                    <span className="viz-bar-fill" style={{ "--share": end > 0 ? Math.max(0, d[key]) / end : 0 } as CSSProperties} />
                  </span>
                  <span className="viz-pair-value">{format(d[key])}</span>
                </span>
              ),
            )}
          </div>
        ))}
        {tip.layer((i) => {
          const d = rows[i]!;
          return (
            <VizTip
              title={d.label}
              rows={[
                { label: aLabel, value: format(d.a), color: "var(--viz-1)" },
                { label: bLabel, value: format(d.b), color: "var(--viz-2)" },
                { label: "Selisih", value: `${d.a - d.b >= 0 ? "+" : ""}${format(d.a - d.b)}` },
              ]}
            />
          );
        })}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * WaffleChart: parts of a whole as a grid of units
 * ------------------------------------------------------------------- */

export interface WaffleChartProps {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  categoryLabel: string;
  valueLabel: string;
  /**
   * "count": one square per unit (a plan of 8 actions draws 8 squares), up to
   * 120 units. "share": one hundred squares, each one percent of the total.
   */
  unit?: "count" | "share";
}

/**
 * Parts of a whole as units in a grid, filled part by part in the legend's
 * order. Near-equal shares stay countable, a count stays a count, and the
 * legend names every part with its value and share. Hover a part and the
 * rest dims.
 */
export function WaffleChart({ label, data, format, categoryLabel, valueLabel, unit = "share" }: WaffleChartProps) {
  const parts = unit === "share" ? groupParts(data) : data.filter((d) => d.value > 0);
  const total = parts.reduce((s, d) => s + d.value, 0);
  const [active, setActive] = useState<number | null>(null);
  const pct = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });
  const share = (v: number) => (total > 0 ? `${pct.format((v / total) * 100)}%` : "0%");
  const counts = unit === "count" && total <= 120 ? parts.map((d) => Math.round(d.value)) : largestRemainder(parts.map((d) => d.value), 100);
  const cells: number[] = counts.flatMap((n, i) => Array.from({ length: n }, () => i));
  const cols = unit === "count" ? Math.min(10, Math.max(4, Math.ceil(Math.sqrt(cells.length)))) : 10;
  return (
    <Shell
      label={label}
      kind="waffle"
      note={unit === "share" ? "Satu kotak, satu persen." : "Satu kotak, satu unit."}
      table={{ caption: label, head: [categoryLabel, valueLabel, "Porsi"], rows: data.map((d) => [d.label, format(d.value), share(d.value)]) }}
    >
      <div className="viz-waffle">
        <div className="viz-plot viz-waffle-grid" style={{ "--cols": cols } as CSSProperties} aria-hidden="true" data-active={active === null ? undefined : ""}>
          {cells.map((part, i) => (
            <span key={i} className="viz-waffle-cell" data-on={active === part ? "" : undefined} style={{ background: PART_COLORS[part % PART_COLORS.length] }} />
          ))}
        </div>
        <ul className="viz-donut-legend">
          {parts.map((d, i) => (
            <li key={d.label}>
              <button
                type="button"
                className="viz-donut-key"
                data-active={active === i ? "" : undefined}
                aria-pressed={active === i}
                onPointerEnter={() => setActive(i)}
                onPointerLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                onClick={() => setActive(active === i ? null : i)}
              >
                <span className="viz-swatch" style={{ background: PART_COLORS[i % PART_COLORS.length] }} aria-hidden="true" />
                <span className="viz-donut-name">{d.label}</span>
                <span className="viz-donut-num">{format(d.value)}</span>
                <span className="viz-donut-share">{share(d.value)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}

/** Whole units that sum to `total`, each as close to its share as the sum allows. */
export function largestRemainder(values: number[], total: number): number[] {
  const sum = values.reduce((s, v) => s + v, 0);
  if (sum <= 0) return values.map(() => 0);
  const exact = values.map((v) => (v / sum) * total);
  const out = exact.map(Math.floor);
  let left = total - out.reduce((s, v) => s + v, 0);
  const order = exact.map((e, i) => [e - Math.floor(e), i] as const).sort((a, b) => b[0] - a[0]);
  for (const [, i] of order) {
    if (left <= 0) break;
    out[i]! += 1;
    left -= 1;
  }
  return out;
}

/* ---------------------------------------------------------------------
 * StepChart: a process whose counts do not only fall
 * ------------------------------------------------------------------- */

export interface StepChartProps {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  valueLabel: string;
}

/**
 * The steps of a process in their order as a stair line: each step holds its
 * own count, so a step that grows (a stage the source counts from another
 * path) rises honestly instead of being drawn as a widening funnel.
 */
export function StepChart({ label, data, format, valueLabel }: StepChartProps) {
  const first = data[0]?.value ?? 0;
  const pct = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 });
  const rows = data.map((d, i) => ({
    label: d.label,
    value: d.value,
    ofFirst: first > 0 ? `${pct.format((d.value / first) * 100)}%` : "",
    ofPrev: i > 0 && data[i - 1]!.value > 0 ? `${pct.format((d.value / data[i - 1]!.value) * 100)}%` : "",
  }));
  const vals = data.map((d) => d.value);
  const span = Math.max(...vals) - Math.min(...vals);
  const lo = Math.max(0, Math.min(...vals) - span * 0.25);
  const hi = Math.max(...vals) + span * 0.15 || 1;
  const ticks = threeTicks(lo, hi);
  const compact = compactFormat(format);
  const rises = rows.filter((r, i) => i > 0 && r.value > data[i - 1]!.value).map((r) => r.label);
  const nameWidth = Math.min(132, Math.max(...data.map((d) => Math.max(...d.label.split(/\s+/).map((w) => textWidthBound(w))))) + 12, Math.max(...data.map((d) => textWidthBound(d.label))) + 12);
  return (
    <Shell
      label={label}
      kind="step"
      note={rises.length > 0 ? `Naik di ${rises.join(", ")}: data sumber mencatat tahap ini dari jalur lain; angkanya ditampilkan apa adanya. Sumbu tidak dimulai dari nol.` : "Sumbu tidak dimulai dari nol."}
      table={{ caption: label, head: ["Tahap", valueLabel, "Dari tahap pertama", "Dari tahap sebelumnya"], rows: rows.map((r) => [r.label, format(r.value), r.ofFirst, r.ofPrev]) }}
    >
      <SvgPlot size="md">
        <ComposedChart layout="vertical" responsive style={FILL} data={rows} margin={{ top: 8, right: 48, bottom: 0, left: 0 }} accessibilityLayer={false}>
          <CartesianGrid horizontal={false} />
          <YAxis type="category" dataKey="label" width={nameWidth} tickLine={false} axisLine={false} interval={0} tick={<NameTick width={nameWidth} />} padding={{ top: 12, bottom: 12 }} />
          <XAxis type="number" tickLine={false} axisLine={false} ticks={ticks} domain={[ticks[0]!, ticks[ticks.length - 1]!]} height={28} tick={<EdgeValueTick format={compact} last={ticks[ticks.length - 1]!} first={ticks[0]!} />} />
          <Tooltip
            cursor={{ stroke: "var(--color-border-strong)" }}
            content={({ active, label: l, payload }) => {
              if (!active || !payload?.length) return null;
              const r = rows.find((x) => x.label === l);
              if (!r) return null;
              return (
                <VizTip
                  title={r.label}
                  rows={[
                    { label: valueLabel, value: format(r.value), color: "var(--viz-1)" },
                    ...(r.ofFirst ? [{ label: "Dari tahap pertama", value: r.ofFirst }] : []),
                    ...(r.ofPrev ? [{ label: "Dari tahap sebelumnya", value: r.ofPrev }] : []),
                  ]}
                />
              );
            }}
            isAnimationActive={false}
          />
          <Line
            type="stepAfter"
            dataKey="value"
            name={valueLabel}
            stroke="var(--viz-1)"
            strokeWidth={2}
            dot={{ r: 4, fill: "var(--viz-1)", strokeWidth: 0 }}
            activeDot={{ r: 6, strokeWidth: 0, fill: "var(--viz-1)" }}
            label={<StepLabel values={vals} format={format} />}
            isAnimationActive={false}
          />
        </ComposedChart>
      </SvgPlot>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * PairScatter: own against competitor per group, one point per group
 * ------------------------------------------------------------------- */

export interface PairScatterProps {
  label: string;
  data: PairedDatum[];
  format: VizFormat;
  categoryLabel: string;
  aLabel: string;
  bLabel: string;
}

/**
 * Each group as one point: own value across, competitor value up, on one
 * scale, with the line where the two are equal. Points under the line are
 * groups where own is higher; above it, the competitor is. Hover a point for
 * its name and both values.
 */
export function PairScatter({ label, data, format, categoryLabel, aLabel, bLabel }: PairScatterProps) {
  const all = data.flatMap((d) => [d.a, d.b]);
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.05 || 1;
  const domain: [number, number] = [lo - pad, hi + pad];
  const width = tickAxisWidth(all, format);
  const above = data.filter((d) => d.b > d.a).length;
  return (
    <Shell
      label={label}
      kind="scatter"
      note={`Garis: ${aLabel.toLowerCase()} sama dengan ${bLabel.toLowerCase()}. ${above} dari ${data.length} titik di atas garis, ${bLabel.toLowerCase()} lebih tinggi.`}
      table={{ caption: label, head: [categoryLabel, aLabel, bLabel], rows: data.map((d) => [d.label, format(d.a), format(d.b)]) }}
    >
      <SvgPlot size="md">
        <ScatterChart responsive style={FILL} margin={{ top: 8, right: 16, bottom: 0, left: 0 }} accessibilityLayer={false}>
          <CartesianGrid />
          <XAxis type="number" dataKey="a" name={aLabel} domain={domain} tickLine={false} axisLine={false} tickCount={5} height={28} tick={<ValueTick format={format} />} />
          <YAxis type="number" dataKey="b" name={bLabel} domain={domain} width={width} tickLine={false} axisLine={false} tickCount={5} tick={<ValueTick format={format} />} />
          <ReferenceLine segment={[{ x: domain[0], y: domain[0] }, { x: domain[1], y: domain[1] }]} stroke="var(--color-ink-muted)" strokeDasharray="4 4" />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              const d = active ? (payload?.[0]?.payload as PairedDatum | undefined) : undefined;
              if (!d) return null;
              return (
                <VizTip
                  title={d.label}
                  rows={[
                    { label: aLabel, value: format(d.a), color: "var(--viz-1)" },
                    { label: bLabel, value: format(d.b), color: "var(--viz-2)" },
                    { label: "Selisih", value: `${d.a - d.b >= 0 ? "+" : ""}${format(d.a - d.b)}` },
                  ]}
                />
              );
            }}
            isAnimationActive={false}
          />
          <Scatter data={data} fill="var(--viz-1)" isAnimationActive={false} />
        </ScatterChart>
      </SvgPlot>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * DeviationList: each category's distance from the average of the categories
 * ------------------------------------------------------------------- */

export interface DeviationListProps {
  label: string;
  data: VizDatum[];
  format: VizFormat;
  categoryLabel: string;
  valueLabel: string;
  limit?: number;
}

/** A difference with its sign: "+5,4 menit", "-2,5 menit", "0". */
function signed(diff: number, format: VizFormat): string {
  if (diff === 0) return format(0);
  return `${diff > 0 ? "+" : "-"}${format(Math.abs(diff))}`;
}

/**
 * Averages, rates and durations per category: never summed, so each is read
 * against the average of the categories. A bar grows right from the
 * average for a category above it and left (second hue) for one below, its
 * length the difference on one scale whose ends and centre are printed
 * under the rows. Every row prints its value and its signed difference, and
 * the note states the whole spread in the measure's own unit and as a share
 * of the average, so a small spread never reads as a large one.
 */
export function DeviationList({ label, data, format, categoryLabel, valueLabel, limit = 10 }: DeviationListProps) {
  const tip = useHoverTip();
  const ordered = [...data].sort((a, b) => b.value - a.value);
  const rows = ordered.slice(0, limit);
  const mean = data.reduce((s, d) => s + d.value, 0) / Math.max(1, data.length);
  const reach = niceEnd(Math.max(...rows.map((d) => Math.abs(d.value - mean)), 0));
  const values = data.map((d) => d.value);
  const spread = Math.max(...values) - Math.min(...values);
  const share = mean !== 0 ? new Intl.NumberFormat("id-ID", { maximumFractionDigits: 1 }).format((spread / Math.abs(mean)) * 100) : null;
  const note = [
    `Batang adalah selisih dari rata-rata ${format(mean)}.`,
    `Tertinggi dan terendah berselisih ${format(spread)}${share !== null ? ` (${share}% dari rata-rata)` : ""}.`,
    ordered.length > rows.length ? `${rows.length} teratas dari ${ordered.length}.` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Shell
      label={label}
      kind="deviation"
      note={note}
      table={{
        caption: label,
        head: [categoryLabel, valueLabel, "Selisih dari rata-rata"],
        rows: ordered.map((d) => [d.label, format(d.value), signed(d.value - mean, format)]),
      }}
    >
      <div className="viz-plot viz-devs" ref={tip.plot}>
        {rows.map((d, i) => {
          const diff = d.value - mean;
          const style = { "--share": reach > 0 ? Math.min(1, Math.abs(diff) / reach) : 0 } as CSSProperties;
          return (
            <div className="viz-dev-row" key={`${d.label}-${i}`} data-below={diff < 0 ? "" : undefined} {...tip.bind(i)}>
              <span className="viz-bar-name">{d.label}</span>
              <span className="viz-dev-track" aria-hidden="true">
                <span className="viz-dev-fill" style={style} />
              </span>
              <span className="viz-dev-figures">
                <span className="viz-bar-value">{format(d.value)}</span>
                <span className="viz-dev-diff">{signed(diff, format)}</span>
              </span>
            </div>
          );
        })}
        <div className="viz-dev-axis" aria-hidden="true">
          <span className="viz-dev-scale">
            <span>{signed(-reach, format)}</span>
            <span>{`rata-rata ${format(mean)}`}</span>
            <span>{signed(reach, format)}</span>
          </span>
        </div>
        {tip.layer((i) => {
          const d = rows[i]!;
          const diff = d.value - mean;
          return (
            <VizTip
              title={d.label}
              rows={[
                { label: valueLabel, value: format(d.value), color: diff < 0 ? "var(--viz-3)" : "var(--viz-1)" },
                { label: "Selisih dari rata-rata", value: signed(diff, format) },
              ]}
            />
          );
        })}
      </div>
    </Shell>
  );
}

/* ---------------------------------------------------------------------
 * XYScatter: two measures per category, one point each
 * ------------------------------------------------------------------- */

export interface XYDatum {
  label: string;
  x: number;
  y: number;
}

export interface XYScatterProps {
  label: string;
  data: XYDatum[];
  xLabel: string;
  yLabel: string;
  xFormat: VizFormat;
  yFormat: VizFormat;
  categoryLabel: string;
}

/**
 * Two measures of the same categories (distribution and out-of-stock per
 * area) as one point per category, each named beside its dot, with the
 * medians as crosshairs so the quadrants read at a glance.
 */
export function XYScatter({ label, data, xLabel, yLabel, xFormat, yFormat, categoryLabel }: XYScatterProps) {
  const median = (v: number[]) => {
    const s2 = [...v].sort((a, b) => a - b);
    const m = Math.floor(s2.length / 2);
    return s2.length % 2 ? s2[m]! : (s2[m - 1]! + s2[m]!) / 2;
  };
  const xs = data.map((d) => d.x);
  const ys = data.map((d) => d.y);
  const pad = (v: number[]) => (Math.max(...v) - Math.min(...v)) * 0.12 || Math.abs(v[0] ?? 1) * 0.1 || 1;
  const xd: [number, number] = [Math.min(...xs) - pad(xs), Math.max(...xs) + pad(xs)];
  const yd: [number, number] = [Math.min(...ys) - pad(ys), Math.max(...ys) + pad(ys)];
  const xt = valueTicks(xd[0], xd[1], xFormat);
  // A point's name sits right of it, left when a close neighbour already holds the right, and only in the tooltip when both sides are taken.
  const nx = (v: number) => (v - xd[0]) / (xd[1] - xd[0]);
  const ny = (v: number) => (v - yd[0]) / (yd[1] - yd[0]);
  const sides: ("right" | "left" | "none")[] = [];
  // At most six names print: the points furthest from the medians, the ones a reader asks about; the rest name themselves on hover.
  const mx = median(xs);
  const my = median(ys);
  const named = new Set(
    data
      .map((d, i) => [Math.hypot(nx(d.x) - nx(mx), ny(d.y) - ny(my)), i] as const)
      .sort((a, b) => b[0] - a[0])
      .slice(0, 6)
      .map(([, i]) => i),
  );
  data.forEach((d, i) => {
    if (!named.has(i)) {
      sides.push("none");
      return;
    }
    const near = (side: "right" | "left") =>
      data.some((o, j) => j < i && sides[j] === side && Math.abs(ny(o.y) - ny(d.y)) < 0.08 && Math.abs(nx(o.x) - nx(d.x)) < 0.22);
    const nearAny = data.some((o, j) => j < i && Math.abs(ny(o.y) - ny(d.y)) < 0.08 && Math.abs(nx(o.x) - nx(d.x)) < 0.22);
    const prefer: "right" | "left" = nx(d.x) > 0.6 ? "left" : "right";
    const other: "right" | "left" = prefer === "left" ? "right" : "left";
    sides.push(!nearAny ? prefer : !near(prefer) ? prefer : !near(other) && (other === "right" ? nx(d.x) < 0.6 : nx(d.x) > 0.25) ? other : "none");
  });
  const yt = valueTicks(yd[0], yd[1], yFormat);
  return (
    <Shell
      label={label}
      kind="scatter"
      note={`Satu titik per ${categoryLabel.toLowerCase()}; enam terjauh dari tengah diberi nama, sisanya muncul saat disorot. Garis putus: median tiap sumbu.`}
      table={{ caption: label, head: [categoryLabel, xLabel, yLabel], rows: data.map((d) => [d.label, xFormat(d.x), yFormat(d.y)]) }}
    >
      <SvgPlot size="md">
        <ScatterChart responsive style={FILL} margin={{ top: 16, right: 24, bottom: 8, left: 0 }} accessibilityLayer={false}>
          <CartesianGrid />
          <XAxis type="number" dataKey="x" name={xLabel} ticks={xt} domain={[xt[0]!, xt[xt.length - 1]!]} tickLine={false} axisLine={false} height={40} tick={<EdgeValueTick format={xFormat} last={xt[xt.length - 1]!} first={xt[0]!} />} label={{ value: xLabel, position: "insideBottom", offset: -4, className: "viz-tick" }} />
          <YAxis type="number" dataKey="y" name={yLabel} ticks={yt} domain={[yt[0]!, yt[yt.length - 1]!]} width={tickAxisWidth(yt, yFormat)} tickLine={false} axisLine={false} tick={<ValueTick format={yFormat} />} />
          <ReferenceLine x={median(xs)} stroke="var(--color-border-strong)" strokeDasharray="4 4" />
          <ReferenceLine y={median(ys)} stroke="var(--color-border-strong)" strokeDasharray="4 4" />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              const d = active ? (payload?.[0]?.payload as XYDatum | undefined) : undefined;
              if (!d) return null;
              return (
                <VizTip
                  title={d.label}
                  rows={[
                    { label: xLabel, value: xFormat(d.x), color: "var(--viz-1)" },
                    { label: yLabel, value: yFormat(d.y), color: "var(--viz-1)" },
                  ]}
                />
              );
            }}
            isAnimationActive={false}
          />
          <Scatter data={data} fill="var(--viz-1)" isAnimationActive={false} label={<PointLabel sides={sides} names={data.map((d) => d.label)} />} />
        </ScatterChart>
      </SvgPlot>
    </Shell>
  );
}
